/**
 * Re-exports marketing and SEO copy from `platformCopy.json` for use in React.
 *
 * Edit copy in `platformCopy.json` only — do not add string literals here except
 * computed values (e.g. `PLATFORM_OG_IMAGE`, `JSON_LD_FAQ`).
 *
 * @see src/constants/README.md for JSON structure and editing workflow
 */
import copy from "./platformCopy.json";
import { getGitHubAppInstallUrl } from "../common/utils/githubAppInstallUrl";

export type FaqListItem = {
  label?: string;
  text: string;
};

export type FaqBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: FaqListItem[] }
  | {
      type: "linkParagraph";
      before: string;
      href: string;
      linkText: string;
      after?: string;
      external?: boolean;
    }
  | {
      type: "emailParagraph";
      before: string;
      email: string;
      after?: string;
    };

export type FaqEntry = {
  question: string;
  blocks: FaqBlock[];
  includeInJsonLd?: boolean;
  schemaAnswer?: string;
};

export type StartPageOption = {
  id: string;
  badge?: string;
  title: string;
  features: string[];
  ctaLabel: string;
  ctaType: "external" | "internal";
  internalPath?: string;
  helperText?: string;
  disabledTooltip?: string;
};

export type StartPageCopy = {
  title: string;
  subtitle: string;
  signInPrompt: string;
  signInLinkText: string;
  contactPrefix: string;
  pageTitle: string;
  pageDescription: string;
  githubAppInstallUrl: string;
  options: StartPageOption[];
};

export const PLATFORM_SITE_URL = copy.siteUrl;
export const PLATFORM_CONTACT_EMAIL = copy.contactEmail;
export const ARCHITECTURAL_INTELLIGENCE_BLOG_URL =
  copy.architecturalIntelligenceBlogUrl;

export const PLATFORM_TAGLINE = copy.platformTagline;
export const PLATFORM_DEFAULT_TITLE = copy.platformDefaultTitle;
export const PLATFORM_META_DESCRIPTION = copy.platformMetaDescription;
export const PLATFORM_ELEVATOR_DESCRIPTION = copy.platformElevatorDescription;
export const PLATFORM_OG_IMAGE = `${copy.siteUrl}${copy.platformOgImagePath}`;

export const MANIFEST_NAME = copy.manifestName;
export const MANIFEST_DESCRIPTION = copy.manifestDescription;

export const HERO_TITLE_LINE_1 = copy.heroTitleLine1;
export const HERO_TITLE_LINE_2 = copy.heroTitleLine2;
export const HERO_SUBTEXT = copy.heroSubtext;
export const HERO_TALK_TO_US_SUBTEXT = copy.heroTalkToUsSubtext;
export const HERO_PRODUCT_LINK_ARIA_LABEL = copy.heroProductLinkAriaLabel;
export const HERO_CONNECT_GITHUB_LABEL = copy.heroConnectGithubLabel;
export const HERO_CONNECT_GITHUB_ARIA_LABEL = copy.heroConnectGithubAriaLabel;
export const HERO_GET_STARTED_LABEL = copy.heroGetStartedLabel;
export const HERO_GET_STARTED_ARIA_LABEL = copy.heroGetStartedAriaLabel;
export const HOMEPAGE_GITHUB_APP_SEO_NAV_LABEL =
  copy.homepageGithubAppSeoNavLabel;
export const HOMEPAGE_GITHUB_APP_LEARN_MORE_CTA_LABEL =
  copy.homepageGithubAppLearnMoreCtaLabel;
export const HOMEPAGE_GITHUB_APP_LEARN_MORE_CTA_ARIA_LABEL =
  copy.homepageGithubAppLearnMoreCtaAriaLabel;

export const AUTH_GITHUB_APP_HELPER_SIGNUP = copy.authGithubAppHelperSignup;
export const AUTH_GITHUB_APP_HELPER_LOGIN = copy.authGithubAppHelperLogin;
export const LOGIN_MAINTENANCE_TOOLTIP = copy.loginMaintenanceTooltip;
export const LOGIN_MAINTENANCE_BANNER = copy.loginMaintenanceBanner;

export const START_PAGE = copy.startPage as StartPageCopy;
export const START_PAGE_OPTIONS = copy.startPage.options as StartPageOption[];
export const GITHUB_APP_INSTALL_URL =
  typeof window === "undefined"
    ? copy.startPage.githubAppInstallUrl
    : getGitHubAppInstallUrl();

export const FOOTER_TAGLINE_LINE_1 = copy.footerTaglineLine1;
export const FOOTER_TAGLINE_LINE_2 = copy.footerTaglineLine2;
export const FOOTER_COPYRIGHT_NAME = copy.footerCopyrightName;

export const AUTO_GENERATED_TITLE = copy.autoGeneratedTitle;
export const AUTO_GENERATED_SUBTEXT = copy.autoGeneratedSubtext;

export type SupportedAgentsCopy = {
  titleLine1: string;
  titleLine2: string;
  description: string;
  boxLabel: string;
  badge: string;
  items: string[];
};

export const SUPPORTED_AGENTS = copy.supportedAgents as SupportedAgentsCopy;

export const DEMO_PAGE_TITLE = copy.demoPageTitle;
export const DEMO_PAGE_DESCRIPTION = copy.demoPageDescription;
export const DEMO_VIDEO_URL = copy.demoVideoUrl;
export const DEMO_VIDEO_META = {
  name: copy.demoPageTitle,
  description: copy.demoPageDescription,
  contentUrl: copy.demoVideoUrl,
  uploadDate: copy.demoVideoUploadDate,
  duration: copy.demoVideoDuration,
  thumbnailUrl: `${copy.siteUrl}${copy.platformOgImagePath}`,
} as const;

export const PRODUCT_PAGE_TITLE = copy.productPageTitle;
export const PRODUCT_PAGE_DESCRIPTION = copy.productPageDescription;
export const PRODUCT_PAGE_SECONDARY_DESCRIPTION =
  copy.productPageSecondaryDescription;

export type GithubAppFeature = {
  title: string;
  description: string;
};

export type GithubAppStat = {
  value: string;
  label: string;
};

export type GithubAppStep = {
  step: string;
  title: string;
  description: string;
};

export type GithubAppPageCopy = {
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  subtitle: string;
  primaryCtaLabel: string;
  secondaryCtaLabel: string;
  stats: GithubAppStat[];
  problemTitle: string;
  problemPoints: string[];
  solutionTitle: string;
  solutionPoints: string[];
  featuresTitle: string;
  features: GithubAppFeature[];
  heroVisualCaption: string;
  heroVisualBadge: string;
  trustLine: string;
  howItWorksTitle: string;
  howItWorksSteps: GithubAppStep[];
  governanceEyebrow: string;
  governanceTitle: string;
  governanceBody: string;
  governancePoints: VibeGovernancePoint[];
  calloutTitle: string;
  calloutBody: string;
};

export const GITHUB_APP_PAGE_TITLE = copy.githubAppPageTitle;
export const GITHUB_APP_PAGE_DESCRIPTION = copy.githubAppPageDescription;
export const GITHUB_APP_PAGE = copy.githubAppPage as GithubAppPageCopy;

export type VibeGovernanceIcon = "drift" | "boundary" | "suggest" | "pulse";

export type VibeGovernancePoint = {
  icon: VibeGovernanceIcon;
  label: string;
  text: string;
};

export type VibeGovernanceSectionCopy = {
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  subtitle: string;
  points: VibeGovernancePoint[];
  ctaLabel: string;
  ctaAriaLabel: string;
};

export const VIBE_GOVERNANCE_SECTION = copy.vibeGovernanceSection as VibeGovernanceSectionCopy;

export type GovernancePageCopy = {
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  subtitle: string;
  secondaryCtaLabel: string;
  installLinkLabel: string;
  trustLine: string;
  problemTitle: string;
  problemPoints: string[];
  solutionTitle: string;
  solutionPoints: string[];
  exampleEyebrow: string;
  exampleTitle: string;
  exampleBody: string;
  calloutTitle: string;
  calloutBody: string;
};

export const GOVERNANCE_PAGE_TITLE = copy.governancePageTitle;
export const GOVERNANCE_PAGE_DESCRIPTION = copy.governancePageDescription;
export const GOVERNANCE_PAGE = copy.governancePage as GovernancePageCopy;

export type VibeCodeCheckMistake = {
  title: string;
  description: string;
};

export type VibeCodeCheckPageCopy = {
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  subtitle: string;
  primaryCtaLabel: string;
  secondaryCtaLabel: string;
  installLinkLabel: string;
  trustLine: string;
  mistakesTitle: string;
  mistakes: VibeCodeCheckMistake[];
  scopeEyebrow: string;
  scopeTitle: string;
  scopeBody: string;
  calloutTitle: string;
  calloutBody: string;
};

export const VIBE_CODE_CHECK_PAGE_TITLE = copy.vibeCodeCheckPageTitle;
export const VIBE_CODE_CHECK_PAGE_DESCRIPTION = copy.vibeCodeCheckPageDescription;
export const VIBE_CODE_CHECK_PAGE = copy.vibeCodeCheckPage as VibeCodeCheckPageCopy;

export type ImprovementIcon =
  | "security"
  | "scalability"
  | "reliability"
  | "maintainability"
  | "architecture";

export type ImprovementPoint = {
  icon: ImprovementIcon;
  label: string;
  text: string;
};

export type WhatWeImproveSectionCopy = {
  eyebrow: string;
  title: string;
  subtitle: string;
  points: ImprovementPoint[];
};

export const WHAT_WE_IMPROVE_SECTION =
  copy.whatWeImproveSection as WhatWeImproveSectionCopy;

export const OPEN_SOURCE_PAGE_TITLE = copy.openSourcePageTitle;
export const OPEN_SOURCE_PAGE_DESCRIPTION = copy.openSourcePageDescription;
export const OPEN_SOURCE_PAGE_HEADING = copy.openSourcePageHeading;
export const OPEN_SOURCE_PAGE_BODY = copy.openSourcePageBody;

export type ProjectPageCopy = {
  titleTemplate: string;
  descriptionTemplate: string;
  headingTemplate: string;
  aboutHeadingTemplate: string;
  componentsHeadingTemplate: string;
  componentsIntroTemplate: string;
  canvasCtaHeadingTemplate: string;
  canvasCtaBodyTemplate: string;
  canvasCtaLabelTemplate: string;
  githubCtaLabelTemplate: string;
  howGeneratedHeading: string;
  howGeneratedBody: string;
  howGeneratedCtaLabel: string;
  otherProjectsHeading: string;
  backToGalleryLabel: string;
  breadcrumbHomeLabel: string;
  breadcrumbGalleryLabel: string;
};

export const PROJECT_PAGE = copy.projectPage as ProjectPageCopy;

/** Fills `{name}` placeholders in `projectPage` templates. */
export const withProjectName = (template: string, name: string): string =>
  template.split("{name}").join(name);

export type DashboardProjectTabsCopy = {
  personalAlongsideGithub: string;
  personalOnly: string;
  githubSingleOrganization: string;
  githubNamedOrganizationTemplate: string;
};

export const DASHBOARD_PROJECT_TABS =
  copy.dashboardProjectTabs as DashboardProjectTabsCopy;

/** Fills `{orgName}` in the named GitHub organization tab tooltip. */
export const withDashboardOrgName = (orgName: string): string =>
  DASHBOARD_PROJECT_TABS.githubNamedOrganizationTemplate
    .split("{orgName}")
    .join(orgName);

export const IN_PRESS_PAGE_TITLE = copy.inPressPageTitle;
export const IN_PRESS_PAGE_DESCRIPTION = copy.inPressPageDescription;

export const PRICING_PAGE_TITLE = copy.pricingPageTitle;
export const PRICING_PAGE_DESCRIPTION = copy.pricingPageDescription;

export type PricingPlanCopy = {
  amount: string;
};

export type TeamPricingPlanCopy = PricingPlanCopy & {
  additionalSeat: string;
};

/** Paid-plan prices and extra-seat lines. Shared by `/pricing` and the purchase modal. */
export const PRICING_PLANS = copy.pricingPlans as {
  explorer_monthly: PricingPlanCopy;
  solo_monthly: PricingPlanCopy;
  team_monthly: TeamPricingPlanCopy;
  team_annual: TeamPricingPlanCopy;
};

export const CLOUD_SCANNER_PAGE_TITLE = copy.cloudScannerPageTitle;
export const CLOUD_SCANNER_PAGE_DESCRIPTION = copy.cloudScannerPageDescription;

export type LabelledPoint = {
  label: string;
  text: string;
};

export type JigsawAiPageCopy = {
  pageTitle: string;
  pageDescription: string;
  eyebrow: string;
  heading: string;
  intro: string;
  namesHeading: string;
  namesBody: string[];
  productHeading: string;
  productBody: string;
  productPoints: LabelledPoint[];
  diagramHeading: string;
  diagramBody: string[];
  diagramPoints: LabelledPoint[];
  diagramCtaText: string;
  otherHeading: string;
  otherIntro: string;
  otherItems: LabelledPoint[];
  ctaHeading: string;
  ctaBody: string;
  ctaPrimaryLabel: string;
  ctaSecondaryLabel: string;
  ctaTertiaryLabel: string;
};

export const JIGSAW_AI_PAGE = copy.jigsawAiPage as JigsawAiPageCopy;

export const NOSCRIPT_INTRO_BODY = copy.noscriptIntroBody;
export const NOSCRIPT_HOW_IT_WORKS = copy.noscriptHowItWorks;

export const PLATFORM_FAQ = copy.faq as FaqEntry[];

export const JSON_LD_FAQ = PLATFORM_FAQ.filter(
  (entry): entry is FaqEntry & { schemaAnswer: string } =>
    entry.includeInJsonLd === true && typeof entry.schemaAnswer === "string"
).map(({ question, schemaAnswer }) => ({
  question,
  answer: schemaAnswer,
}));

export default copy;
